import type { AfterworkItemModel } from '../afterwork-item.model';
import type { OtherEventItemModel } from '../other-event-item.model';
import type { AgendaItemModel } from './agenda-item.model';
import type { DataItem } from './data-item.model';
import type { LinkItemModel } from './link-item.model';
import type { LocationCityCountryModel } from './location/location-city-country.model';
import type { RegistrationDeadlineItemModel } from './registration-deadline-item.model';

export interface EventItem<T extends EventType> extends DataItem {
  title: string;
  type: T;
  category: EventCategory;
  startDate: Date;
  endDate: Date;
  moodImage?: {
    url: string;
    width?: number;
    height: number;
    thumbnailUrl?: string;
  };
}

export interface EventWithLocationItem<T extends EventType> extends EventItem<T> {
  location?: LocationCityCountryModel;
}

export interface EventWithAgendaItems<T extends EventType> extends EventItem<T> {
  agenda: AgendaItemModel[];
}

export interface EventWithKeywordItems<T extends EventType> extends EventItem<T> {
  keywords: string[];
}

export type MarkdownContent = string;

export interface EventWithDescriptionMarkDownItem<T extends EventType> extends EventItem<T> {
  description: MarkdownContent;
}

export interface EventWithTravelInformationItem<T extends EventType> extends EventItem<T> {
  travelInformation: MarkdownContent;
}

export interface EventWithRegistrationDeadlineItem<T extends EventType> extends EventItem<T> {
  registrationDeadline: RegistrationDeadlineItemModel;
}

export interface EventWithLinkItems<T extends EventType> extends EventItem<T> {
  links?: LinkItemModel[];
}

export type EventType = 'Afterwork' | 'Offsite' | 'Weihnachtsfeier' | 'NewsAtFourEnergy' | 'LearningAtLunch' | 'Other';
export type EventCategory = 'information' | 'knowledge' | 'socializing' | 'highlight' | 'special';

export const isEventWithAgendaField = (event: EventItem<EventType>): event is EventWithAgendaItems<EventType> => {
  return Object.hasOwn(event, 'agenda');
};

export const isAfterworkEvent = (data: EventItem<EventType>): data is AfterworkItemModel => {
  return data.type === 'Afterwork';
};

export const isEventWithLocation = (event: EventItem<EventType>): event is EventWithLocationItem<EventType> => {
  return Object.hasOwn(event, 'location');
};

export const isEventWithKeywords = (event: EventItem<EventType>): event is EventWithKeywordItems<EventType> => {
  return Object.hasOwn(event, 'keywords');
};

export const isEventWithTravelInformation = (
  event: EventItem<EventType>
): event is EventWithTravelInformationItem<EventType> => {
  return Object.hasOwn(event, 'travelInformation');
};

export const isEventWithRegistrationDeadlineField = <T extends EventType>(
  data: EventItem<T>
): data is EventWithRegistrationDeadlineItem<T> => {
  return Object.hasOwn(data, 'registrationDeadline');
};

export const isEventEventWithDescriptionMarkDownField = <T extends EventType>(
  data: EventItem<T>
): data is EventWithDescriptionMarkDownItem<T> => {
  return Object.hasOwn(data, 'description');
};

export const isEventWithLinks = <T extends EventType>(data: EventItem<T>): data is EventWithLinkItems<T> => {
  return Object.hasOwn(data, 'links');
};

export const isOtherEvent = (data: EventItem<EventType>): data is OtherEventItemModel => {
  return data.type === 'Other';
};
